import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout as AntLayout, Menu, Avatar, Dropdown, message, Badge, Drawer, Tooltip } from 'antd';
import { useQuery } from 'react-query';
import {
    DashboardOutlined,
    UserOutlined,
    TeamOutlined,
    BookOutlined,
    DollarOutlined,
    FileTextOutlined,
    BarChartOutlined,
    SafetyCertificateOutlined,
    BankOutlined,
    LogoutOutlined,
    SettingOutlined,
    GlobalOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    FileImageOutlined,
    MessageOutlined,
    ApiOutlined,
    AuditOutlined,
    BellOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import LanguageSwitcher from './LanguageSwitcher';
import api from '../utils/api';
import config from '../config';

const { Header, Content, Sider } = AntLayout;

const Layout = () => {// Debugging line
    const [collapsed, setCollapsed] = useState(false);
    const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, updateUser } = useAuthStore();
    const { t, i18n } = useTranslation();

    // Check if current language is RTL
    const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

    // Fetch notification unread count
    const { data: notifCounts } = useQuery(
        'notification-counts',
        () => api.get('/payment-notifications/count/by-status').then(r => r.data),
        { refetchInterval: 60000, enabled: !!user?.role && user.role !== 'dataentry' }
    );
    const unreadCount = notifCounts?.unread || 0;

    // Fetch academy data to ensure logo is loaded
    useEffect(() => {
        const fetchAcademyData = async () => {
            if (user?.academy?._id) {
                try {
                    const response = await api.get(`/academy/${user.academy._id}`);
                    if (response.data.data) {
                        updateUser({ ...user, academy: response.data.data });
                    }
                } catch (error) {
                    console.error('Failed to fetch academy data:', error);
                }
            }
        };
        fetchAcademyData();
    }, []);

    // Check screen size
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 992);
            if (window.innerWidth < 992) {
                setCollapsed(true);
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleLogout = () => {
        logout();
        message.success('Logged out successfully');
        navigate('/login');
    };

    const userMenu = [
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: t('profile'),
            onClick: () => navigate('/profile'),
        },
        {
            key: 'settings',
            icon: <SettingOutlined />,
            label: t('settings'),
            onClick: () => navigate('/settings'),
        },
        {
            type: 'divider',
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: t('logout'),
            onClick: handleLogout,
        },
    ];

    const menuItems = [
        ...(user?.role !== 'dataentry'
            ? [
                {
                    key: 'dashboard',
                    icon: <DashboardOutlined style={{ fontSize: '18px' }} />,
                    label: t('dashboard'),
                    onClick: () => {
                        navigate('/dashboard');
                        if (isMobile) setMobileDrawerVisible(false);
                    },
                },
            ]
            : []),
        ...(user?.role === 'superadmin'
            ? [
                {
                    key: 'academies',
                    icon: <GlobalOutlined style={{ fontSize: '18px' }} />,
                    label: t('academies'),
                    onClick: () => {
                        navigate('/academies');
                        if (isMobile) setMobileDrawerVisible(false);
                    },
                },
            ]
            : []),
        {
            key: 'academic',
            icon: <BookOutlined style={{ fontSize: '18px' }} />,
            label: t('academic'),
            children: [
                {
                    key: 'students',
                    icon: <UserOutlined />,
                    label: t('students'),
                    onClick: () => {
                        navigate('/students');
                        if (isMobile) setMobileDrawerVisible(false);
                    },
                },
                {
                    key: 'staff',
                    icon: <TeamOutlined />,
                    label: t('staff'),
                    onClick: () => {
                        navigate('/staff');
                        if (isMobile) setMobileDrawerVisible(false);
                    },
                },
                {
                    key: 'courses',
                    icon: <BookOutlined />,
                    label: t('courses'),
                    onClick: () => {
                        navigate('/courses');
                        if (isMobile) setMobileDrawerVisible(false);
                    },
                },
                {
                    key: 'subjects',
                    icon: <FileTextOutlined />,
                    label: t('subjects'),
                    onClick: () => {
                        navigate('/subjects');
                        if (isMobile) setMobileDrawerVisible(false);
                    },
                },
            ],
        },
        ...(user?.role !== 'dataentry'
            ? [
                {
                    key: 'finance',
                    icon: <DollarOutlined style={{ fontSize: '18px' }} />,
                    label: t('finance'),
                    children: [
                        {
                            key: 'income',
                            label: t('income'),
                            onClick: () => {
                                navigate('/income');
                                if (isMobile) setMobileDrawerVisible(false);
                            },
                        },
                        {
                            key: 'expenses',
                            label: t('expenses'),
                            onClick: () => {
                                navigate('/expenses');
                                if (isMobile) setMobileDrawerVisible(false);
                            },
                        },
                        {
                            key: 'recurring-expenses',
                            label: t('recurringExpenses'),
                            onClick: () => {
                                navigate('/recurring-expenses');
                                if (isMobile) setMobileDrawerVisible(false);
                            },
                        },
                    ],
                },
            ]
            : []),
        ...(user?.role !== 'dataentry'
            ? [
                {
                    key: 'certificates-menu',
                    icon: <SafetyCertificateOutlined style={{ fontSize: '18px' }} />,
                    label: t('certificates'),
                    children: [
                        {
                            key: 'certificates',
                            icon: <SafetyCertificateOutlined />,
                            label: t('certificates'),
                            onClick: () => {
                                navigate('/certificates');
                                if (isMobile) setMobileDrawerVisible(false);
                            },
                        },
                        ...(user?.role === 'admin'
                            ? [
                                {
                                    key: 'certificate-templates',
                                    icon: <FileImageOutlined />,
                                    label: t('certificateTemplates'),
                                    onClick: () => {
                                        navigate('/certificate-templates');
                                        if (isMobile) setMobileDrawerVisible(false);
                                    },
                                },
                            ]
                            : []),
                    ],
                },
                {
                    key: 'invoices',
                    icon: <FileTextOutlined style={{ fontSize: '18px' }} />,
                    label: t('invoices'),
                    onClick: () => {
                        navigate('/invoices');
                        if (isMobile) setMobileDrawerVisible(false);
                    },
                },
                {
                    key: 'payment-notifications',
                    icon: <BellOutlined style={{ fontSize: '18px' }} />,
                    label: t('paymentNotifications') || 'Payment Notifications',
                    onClick: () => {
                        navigate('/payment-notifications');
                        if (isMobile) setMobileDrawerVisible(false);
                    },
                },
            ]
            : []),
        ...(user?.role === 'admin' || user?.role === 'superadmin'
            ? [
                {
                    key: 'reports',
                    icon: <BarChartOutlined style={{ fontSize: '18px' }} />,
                    label: t('reports'),
                    children: [
                        {
                            key: 'general-reports',
                            label: t('generalReports'),
                            onClick: () => {
                                navigate('/reports');
                                if (isMobile) setMobileDrawerVisible(false);
                            },
                        },
                        {
                            key: 'course-report',
                            label: t('courseReport'),
                            onClick: () => {
                                navigate('/course-report');
                                if (isMobile) setMobileDrawerVisible(false);
                            },
                        },
                        {
                            key: 'staff-report',
                            label: t('staffReport'),
                            onClick: () => {
                                navigate('/staff-report');
                                if (isMobile) setMobileDrawerVisible(false);
                            },
                        },
                    ],
                },
            ]
            : []),
        ...(user?.role === 'admin' || user?.role === 'superadmin'
            ? [
                {
                    key: 'users',
                    icon: <TeamOutlined style={{ fontSize: '18px' }} />,
                    label: t('users'),
                    onClick: () => {
                        navigate('/users');
                        if (isMobile) setMobileDrawerVisible(false);
                    },
                },
                {
                    key: 'audit-logs',
                    icon: <AuditOutlined style={{ fontSize: '18px' }} />,
                    label: t('auditLogs'),
                    onClick: () => {
                        navigate('/audit-logs');
                        if (isMobile) setMobileDrawerVisible(false);
                    },
                },
            ]
            : []),
        ...(user?.role === 'admin'
            ? [
                {
                    key: 'online-accounting',
                    icon: <BankOutlined style={{ fontSize: '18px' }} />,
                    label: t('onlineAccounting.title'),
                    children: [
                        {
                            key: 'online-students',
                            icon: <UserOutlined />,
                            label: t('onlineAccounting.students'),
                            onClick: () => {
                                navigate('/online-students');
                                if (isMobile) setMobileDrawerVisible(false);
                            },
                        },
                        {
                            key: 'online-expenses',
                            icon: <DollarOutlined />,
                            label: t('onlineAccounting.expenses'),
                            onClick: () => {
                                navigate('/online-expenses');
                                if (isMobile) setMobileDrawerVisible(false);
                            },
                        },
                        {
                            key: 'online-income',
                            icon: <DollarOutlined />,
                            label: t('onlineAccounting.income'),
                            onClick: () => {
                                navigate('/online-income');
                                if (isMobile) setMobileDrawerVisible(false);
                            },
                        },
                        {
                            key: 'online-reports',
                            icon: <BarChartOutlined />,
                            label: t('onlineAccounting.reports'),
                            onClick: () => {
                                navigate('/online-reports');
                                if (isMobile) setMobileDrawerVisible(false);
                            },
                        },
                    ],
                },
            ]
            : []),
        // {
        //     key: 'social',
        //     icon: <MessageOutlined style={{ fontSize: '18px' }} />,
        //     label: t('Social Messaging'),
        //     children: [
        //         {
        //             key: 'social-messaging',
        //             icon: <MessageOutlined />,
        //             label: t('Messages'),
        //             onClick: () => {
        //                 navigate('/social-messaging');
        //                 if (isMobile) setMobileDrawerVisible(false);
        //             },
        //         },
        //         ...(user?.role === 'admin' || user?.role === 'superadmin'
        //             ? [
        //                 {
        //                     key: 'social-integrations',
        //                     icon: <ApiOutlined />,
        //                     label: t('Integrations'),
        //                     onClick: () => {
        //                         navigate('/social-integrations');
        //                         if (isMobile) setMobileDrawerVisible(false);
        //                     },
        //                 },
        //             ]
        //             : []),
        //     ],
        // },
    ];

    // Get current selected key from location
    const getSelectedKey = () => {
        const path = location.pathname.split('/')[1];
        return path || 'dashboard';
    };

    // Get open keys for submenu based on current path
    const getOpenKeys = () => {
        const path = location.pathname.split('/')[1];
        const openKeys = [];

        // Map paths to their parent menu keys
        if (['students', 'staff', 'courses', 'subjects'].includes(path)) {
            openKeys.push('academic');
        }
        if (['income', 'expenses', 'recurring-expenses'].includes(path)) {
            openKeys.push('finance');
        }
        if (['certificates', 'certificate-templates'].includes(path)) {
            openKeys.push('certificates-menu');
        }
        if (['social-messaging', 'social-integrations'].includes(path)) {
            openKeys.push('social');
        }
        if (['online-students', 'online-expenses', 'online-income', 'online-reports'].includes(path)) {
            openKeys.push('online-accounting');
        }

        return openKeys;
    };

    // Sidebar Content Component
    const SidebarContent = ({ showUserProfile = true }) => (
        <>
            <div
                className="logo"
                style={{
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: '4px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                }}
            >
                {(!collapsed || isMobile) ? (
                    <>
                        {/* {user?.academy?.logo && (
                            <img
                                src={user.academy.logo}
                                alt="Logo"
                                style={{
                                    height: '40px',
                                    width: 'auto',
                                    objectFit: 'contain',
                                    marginBottom: '4px'
                                }}
                            />
                        )} */}
                        <div style={{
                            fontSize: isMobile ? '18px' : '20px',
                            fontWeight: '800',
                            letterSpacing: '0.5px',
                            textAlign: 'center',
                            padding: '0 8px',
                        }}>
                            {user?.academy?.name || 'Paymanga'}
                        </div>
                        <div style={{
                            fontSize: '10px',
                            opacity: 0.7,
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                        }}>
                            {t('institute')}
                        </div>
                    </>
                ) : (
                    user?.academy?.logo ? (
                        <img
                                src={`${config.baseUrl}${user.academy.logo}`}
                            alt="Logo"
                            style={{
                                height: '48px',
                                width: 'auto',
                                objectFit: 'contain'
                            }}
                        />
                    ) : (
                        <div style={{
                            fontSize: '32px',
                            fontWeight: '800',
                        }}>
                            {user?.academy?.name?.substring(0, 2).toUpperCase() || 'PM'}
                        </div>
                    )
                )}
            </div>

            <div style={{ padding: '16px 0', marginBottom: showUserProfile ? '100px' : '0' }}>
                <Menu
                    theme="dark"
                    selectedKeys={[getSelectedKey()]}
                    defaultOpenKeys={getOpenKeys()}
                    mode="inline"
                    items={menuItems}
                    style={{
                        border: 'none',
                        fontSize: '15px',
                    }}
                />
            </div>

        </>
    );

    return (
        <AntLayout style={{ minHeight: '100vh' }}>
            {/* Desktop Sidebar */}
            {!isMobile && (
                <Sider
                    collapsible
                    collapsed={collapsed}
                    onCollapse={setCollapsed}
                    width={280}
                    collapsedWidth={80}
                    breakpoint="lg"
                    style={{
                        overflow: 'auto',
                        height: '100vh',
                        position: 'fixed',
                        ...(isRTL ? { right: 0 } : { left: 0 }),
                        top: 0,
                        bottom: 0,
                    }}
                >
                    <SidebarContent />
                </Sider>
            )}

            {/* Mobile Drawer */}
            {isMobile && (
                <Drawer
                    placement={isRTL ? 'right' : 'left'}
                    onClose={() => setMobileDrawerVisible(false)}
                    open={mobileDrawerVisible}
                    styles={{ body: { padding: 0, background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)' } }}
                    width={280}
                >
                    <div style={{ background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)', minHeight: '100vh' }}>
                        <SidebarContent showUserProfile={false} />
                    </div>
                </Drawer>
            )}

            <AntLayout style={{
                ...(isMobile
                    ? {}
                    : isRTL
                        ? { marginRight: collapsed ? 80 : 280 }
                        : { marginLeft: collapsed ? 80 : 280 }
                ),
                transition: 'all 0.2s'
            }}>
                <Header style={{
                    background: '#fff',
                    padding: isMobile ? '0 12px' : '0 32px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: isMobile ? '0 0 auto' : 1 }}>
                        {isMobile && (
                            <MenuUnfoldOutlined
                                style={{ fontSize: '22px', cursor: 'pointer', color: '#1e293b' }}
                                onClick={() => setMobileDrawerVisible(true)}
                            />
                        )}
                        {!isMobile && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                justifyContent: 'center',
                            }}>
                                {user?.academy?.logo && (
                                    <img
                                        src={`${config.baseUrl}${user.academy.logo}`}
                                        alt="Logo"
                                        style={{
                                            height: '40px',
                                            width: 'auto',
                                            objectFit: 'contain'
                                        }}
                                    />
                                )}
                                <div style={{
                                    fontSize: '24px',
                                    fontWeight: '700',
                                    textAlign: 'center',
                                }}>
                                    <span style={{
                                        background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                    }}>
                                        {user?.academy?.name || 'Language Institute Management System'}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                    {isMobile && user?.academy?.logo && (
                        <div style={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            alignItems: 'center',
                            zIndex: 0
                        }}>
                            <img
                                src={`${config.baseUrl}${user.academy.logo}`}
                                alt="Logo"
                                style={{
                                    height: '32px',
                                    width: 'auto',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '20px', position: 'relative', zIndex: 1 }}>
                        {user?.role !== 'dataentry' && (
                            <Tooltip title={t('paymentNotifications') || 'Payment Notifications'}>
                                <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                                    <div
                                        onClick={() => navigate('/payment-notifications')}
                                        style={{
                                            cursor: 'pointer',
                                            width: 36,
                                            height: 36,
                                            borderRadius: '50%',
                                            background: '#f1f5f9',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'background 0.2s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
                                        onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}
                                    >
                                        <BellOutlined style={{ fontSize: '18px', color: unreadCount > 0 ? '#f59e0b' : '#64748b' }} />
                                    </div>
                                </Badge>
                            </Tooltip>
                        )}
                        <LanguageSwitcher />
                        <Dropdown menu={{ items: userMenu }} placement="bottomRight" trigger={['click']}>
                            <div style={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: isMobile ? '4px 8px' : '8px 16px',
                                borderRadius: '12px',
                                transition: 'all 0.3s',
                                background: '#f8fafc',
                            }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                                onMouseLeave={(e) => e.currentTarget.style.background = '#f8fafc'}
                            >
                                <Avatar
                                    size={isMobile ? 32 : 40}
                                    style={{
                                        background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                                        border: '2px solid #e2e8f0',
                                    }}
                                >
                                    {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                                </Avatar>
                                {!isMobile && (
                                    <div style={{ lineHeight: '1.2' }}>
                                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>
                                            {user?.firstName} {user?.lastName}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#64748b', textTransform: 'capitalize' }}>
                                            {user?.role}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Dropdown>
                    </div>
                </Header>

                <Content style={{
                    margin: isMobile ? '12px 8px' : '24px',
                    padding: isMobile ? '16px 12px' : '32px',
                    background: '#fff',
                    borderRadius: isMobile ? '12px' : '16px',
                    minHeight: 'calc(100vh - 88px)',
                }}>
                    <Outlet />
                </Content>
            </AntLayout>
        </AntLayout>
    );
};

export default Layout;
